import * as React from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import { buttonVariants } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { cn } from "utils";

type CalendarComponents = NonNullable<
  React.ComponentProps<typeof Calendar>["components"]
>;

// Stable replacements for the components the stock shadcn Calendar defines
// inline. Inline definitions get a new identity on every render, so any
// parent re-render remounts the whole react-day-picker subtree — detaching
// day buttons mid-click and swallowing the interaction.
const CalendarRoot: NonNullable<CalendarComponents["Root"]> = ({
  className,
  rootRef,
  ...props
}) => {
  return (
    <div
      data-slot="calendar"
      ref={rootRef}
      className={cn(className)}
      {...props}
    />
  );
};

const CalendarChevron: NonNullable<CalendarComponents["Chevron"]> = ({
  className,
  orientation,
  ...props
}) => {
  if (orientation === "left") {
    return <ChevronLeftIcon className={cn("size-4", className)} {...props} />;
  }

  if (orientation === "right") {
    return <ChevronRightIcon className={cn("size-4", className)} {...props} />;
  }

  return <ChevronDownIcon className={cn("size-4", className)} {...props} />;
};

const CalendarWeekNumber: NonNullable<CalendarComponents["WeekNumber"]> = ({
  children,
  ...props
}) => {
  return (
    <td {...props}>
      <div className="flex size-(--cell-size) items-center justify-center text-center">
        {children}
      </div>
    </td>
  );
};

export const StyledCalendar = ({
  className,
  classNames,
  components,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof Calendar>) => {
  return (
    <Calendar
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        root: "flex flex-col items-center justify-center relative w-fit mx-auto",
        months: "flex flex-col sm:flex-row",
        month: "space-y-4",
        nav: "flex",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "w-6 h-6 bg-transparent p-0 opacity-50 hover:opacity-100",
          "absolute left-1 top-4",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "w-6 h-6 bg-transparent p-0 opacity-50 hover:opacity-100",
          "absolute right-1 top-4",
        ),
        month_caption: "flex justify-center pt-1 items-center",
        caption_label: "text-md font-normal",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex justify-between",
        weekday: `rounded-md font-normal text-[0.8rem] w-9 h-9 xl:w-10 xl:h-10 flex items-center justify-center p-1 m-1 text-sm font-normal text-secondary-400`,
        week: "flex w-full",
        day: cn(
          "relative m-1 p-0 flex h-full w-full items-center justify-center rounded-full text-center text-sm font-normal",
          "aria-selected:opacity-100 [&:has([aria-selected].day-outside)]:bg-accent/50 focus-within:relative focus-within:z-20",
        ),
        day_button: cn(
          "rounded-full",
          "data-[range-end=true]:hover:text-white",
          "data-[range-start=true]:hover:text-white",
          "data-[selected-single=true]:hover:text-white",
          "data-[range-end=true]:rounded-full data-[range-end=true]:rounded-full data-[range-middle=true]:rounded-full data-[range-start=true]:rounded-full data-[range-start=true]:rounded-full",
        ),
        range_start: "rounded-full",
        range_middle: cn(
          "aria-selected:bg-accent aria-selected:text-accent-foreground rounded-full",
        ),
        range_end: "rounded-full",
        selected: cn(
          "data-[selected-single=true]:text-white",
          "bg-primary text-white hover:bg-primary hover:text-white focus:text-primary-foreground",
        ),
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-secondary-200 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Root: CalendarRoot,
        Chevron: CalendarChevron,
        WeekNumber: CalendarWeekNumber,
        ...components,
      }}
      {...props}
    />
  );
};
